import { Checkbox as MuiCheckbox, Dialog, IconButton } from "@mui/material";
import styled, { css } from "styled-components/macro";

export const CfcOrderDetailsStyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    width: calc(100% - 64px);
    max-width: 925px;
    border-radius: 6px;
    overflow: hidden;

    @media (max-width: 800px) {
      width: calc(100% - 24px);
      margin: 12px;
      max-height: calc(100% - 24px);
    }
  }
`;

export const CfcOrderDetailsHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;
  padding: 16px 24px;
  background-color: ${({ theme }) => theme.neutral50};
  border-bottom: 1px solid ${({ theme }) => theme.neutral100};
`;

export const CfcCloseButton = styled(IconButton)`
  && {
    color: ${({ theme }) => theme.redMedium};
  }
`;

export const CfcOrderDetailsTitle = styled.h1`
  margin: 0;
  font-family: bold;
  font-size: 16px;
  line-height: 24px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.redMedium};
`;

export const CfcOrderDetailsBody = styled.section`
  box-sizing: border-box;
  width: 100%;
  max-height: calc(100vh - 180px);
  overflow-y: auto;
  padding: 21px 32px 32px;
  background-color: ${({ theme }) => theme.white};

  @media (max-width: 800px) {
    max-height: calc(100vh - 104px);
    padding: 20px 16px 24px;
  }
`;

export const CfcOrderDetailsMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 8px 16px 0;
  color: ${({ theme }) => theme.blueDark};
  font-family: light;
  font-size: 16px;
  line-height: 24px;

  p {
    min-width: 0;
    margin: 0;
    overflow-wrap: anywhere;
  }

  strong {
    font-family: bold;
    overflow-wrap: anywhere;
  }
`;

export const CfcOrderDetailsIntro = styled.p`
  margin: 0;
  padding: 16px 0 4px;
  color: ${({ theme }) => theme.greyDark};
  font-family: light;
  font-size: 18px;
  line-height: 28px;
`;

export const CfcOrderDetailsPatientGrid = styled.dl`
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr);
  column-gap: 20px;
  row-gap: 4px;
  margin: 0;
  padding-bottom: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    row-gap: 2px;
  }
`;

export const CfcOrderDetailsLabel = styled.dt`
  margin: 0;
  color: ${({ theme }) => theme.greyMedium};
  font-family: light;
  font-size: 14px;
  line-height: 20px;
`;

const cfcOrderDetailsValueStyles = css`
  min-width: 0;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.greyDark};
  font-family: bold;
  font-size: 14px;
  line-height: 20px;
`;

export const CfcOrderDetailsValue = styled.dd`
  margin: 0;
  ${cfcOrderDetailsValueStyles}
`;

export const CfcOrderDetailsEmptyValue = styled.div`
  ${cfcOrderDetailsValueStyles}
`;

export const CfcOrderDetailsSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.neutral100};
`;

export const CfcOrderDetailsLead = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.greyMedium};
  font-family: light;
  font-size: 16px;
  line-height: 24px;
`;

export const CfcOrderDetailsConditionList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 16px 32px;
  margin: 0;
  padding: 0 0 0 27px;
`;

export const CfcOrderDetailsCondition = styled.li`
  color: ${({ theme }) => theme.greyDark};
  font-family: bold;
  font-size: 18px;
  line-height: 28px;
`;

export const CfcOrderDetailsIcdSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 16px 16px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.neutral100};
`;

export const CfcOrderDetailsSubheader = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.greyDark};
  font-family: bold;
  font-size: 20px;
  line-height: 28px;
`;

export const CfcOrderDetailsIcdGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 1fr) minmax(80px, 1fr);
  gap: 16px 38px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

export const CfcOrderDetailsCheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  color: ${({ theme }) => theme.greyDark};
  font-family: light;
  font-size: 16px;
  line-height: 16px;

  .MuiCheckbox-root {
    padding: 0;
  }

  .MuiCheckbox-root.Mui-checked,
  .MuiCheckbox-root.Mui-disabled.Mui-checked {
    color: ${({ theme }) => theme.redMedium};
  }
`;

export const CfcOrderDetailsIcdCode = styled.div`
  color: ${({ theme }) => theme.greyMedium};
  font-family: light;
  font-size: 16px;
  line-height: 24px;
`;

export const CfcOrderDetailsOtherIcd = styled.div`
  width: 429px;
  max-width: calc(100% - 36px);
  margin-left: 36px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.greyLight};

  @media (max-width: 600px) {
    width: 100%;
    max-width: 100%;
    margin-left: 0;
  }
`;

export const CfcOrderDetailsOtherIcdLabel = styled.div`
  color: ${({ theme }) => theme.blueDark};
  font-family: light;
  font-size: 14px;
  line-height: 20px;
`;

export const CfcOrderDetailsOtherIcdValue = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.greyDark};
  font-family: light;
  font-size: 16px;
  line-height: 24px;
`;

export const CfcOrderDetailsPhlebotomy = styled.section`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 16px 16px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.neutral100};

  .MuiCheckbox-root {
    padding: 0;
  }

  .MuiCheckbox-root.Mui-checked,
  .MuiCheckbox-root.Mui-disabled.Mui-checked {
    color: ${({ theme }) => theme.redMedium};
  }
`;

export const CfcCheckbox = styled(MuiCheckbox)`
  && {
    color: ${({ theme }) => theme.greyLight};
    &.Mui-checked {
      color: ${({ theme }) => theme.redMedium};
    }
  }
`;

export const CfcPhlebotomyCopy = styled.div`
  display: flex;
  flex-direction: column;
`;

export const CfcPhlebotomyTitle = styled.div`
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  font-family: bold;
  color: ${({ theme }) => theme.blueDark};
  cursor: default;
`;

export const CfcPhlebotomyDescription = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  font-family: light;
  color: ${({ theme }) => theme.greyMedium};
`;
